import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import settingsService from '../../../services/settingsService';
import NotificationToast from '../../../components/ui/NotificationToast';

const LeadSourcesMaster = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadSources();
  }, []);

  const loadSources = async () => {
    try {
      setLoading(true);
      const result = await settingsService.getLeadSources();
      if (result?.success) {
        setSources(result.data || []);
      }
    } catch (error) {
      console.log('Error loading lead sources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEdit = (source) => {
    setEditingId(source.id);
    setFormData({
      name: source.name || '',
      description: source.description || ''
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = editingId
        ? await settingsService.updateLeadSource(editingId, formData)
        : await settingsService.createLeadSource(formData);

      if (result?.success) {
        setNotification({ 
          message: `Lead source ${editingId ? 'updated' : 'created'} successfully`, 
          type: 'success' 
        });
        handleCancel();
        loadSources();
      } else {
        setNotification({ 
          message: result?.error || `Failed to ${editingId ? 'update' : 'create'} lead source`, 
          type: 'error' 
        });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
      console.log('Error saving lead source:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead source?')) {
      return;
    }

    try {
      const result = await settingsService.deleteLeadSource(id);
      if (result?.success) {
        setNotification({ message: 'Lead source deleted successfully', type: 'success' });
        loadSources();
      } else {
        setNotification({ message: result?.error || 'Failed to delete lead source', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
      console.log('Error deleting lead source:', error);
    }
  };

  return (
    <div>
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-lg font-semibold text-foreground">Lead Sources</h4>
          <p className="text-sm text-muted-foreground">Manage lead source options for the CRM system</p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
            <Icon name="Plus" size={16} />
            Add Lead Source
          </Button>
        )}
      </div>

      {showForm && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <h5 className="font-semibold text-foreground mb-4">
            {editingId ? 'Edit Lead Source' : 'Add New Lead Source'}
          </h5>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Source Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Direct Contact, Job Portal, Referral"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <Input
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" className="flex items-center gap-2">
                <Icon name="Save" size={16} />
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading lead sources...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sources.length === 0 ? (
            <div className="col-span-full text-center py-8 text-muted-foreground">
              No lead sources found. Click "Add Lead Source" to create one.
            </div>
          ) : (
            sources.map(source => (
              <div key={source.id} className="p-4 border border-border rounded-lg hover:bg-muted/50">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h5 className="font-semibold text-foreground">{source.name}</h5>
                    {source.description && (
                      <p className="text-sm text-muted-foreground mt-1">{source.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(source)}
                      className="h-8 w-8 p-0"
                      title="Edit"
                    >
                      <Icon name="Edit" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(source.id)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default LeadSourcesMaster;

